/**
 * Step 2: Store Hours Setup
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' }
];

const DEFAULT_HOURS = {
  monday: { is_open: true, open_time: '09:00', close_time: '21:00' },
  tuesday: { is_open: true, open_time: '09:00', close_time: '21:00' },
  wednesday: { is_open: true, open_time: '09:00', close_time: '21:00' },
  thursday: { is_open: true, open_time: '09:00', close_time: '21:00' },
  friday: { is_open: true, open_time: '09:00', close_time: '21:00' },
  saturday: { is_open: true, open_time: '09:00', close_time: '21:00' },
  sunday: { is_open: false, open_time: null, close_time: null }
};

export default function StoreHoursStep({ data, onComplete, onBack }) {
  const [formData, setFormData] = useState({
    ...DEFAULT_HOURS,
    ...(data || {}),
    timezone: data?.timezone || 'America/New_York'
  });
  const [saving, setSaving] = useState(false);

  const handleDayChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  const copyToAll = (sourceDay) => {
    const source = formData[sourceDay];
    const updated = { ...formData };
    DAYS.forEach(day => {
      if (day !== sourceDay) {
        updated[day] = { ...source };
      }
    });
    setFormData(updated);
    toast.success(`Copied ${sourceDay}'s hours to all days`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/retailer/onboarding/store-hours`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('Store hours saved!');
        onComplete(result);
      } else {
        const errors = result.detail?.errors || [result.detail];
        errors.forEach(err => toast.error(err));
      }
    } catch (error) {
      toast.error('Failed to save store hours');
    } finally {
      setSaving(false);
    }
  };

  const formatDayLabel = (day) => {
    return day.charAt(0).toUpperCase() + day.slice(1);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Timezone */}
      <div>
        <Label>Time Zone</Label>
        <Select
          value={formData.timezone}
          onValueChange={(value) => setFormData(prev => ({ ...prev, timezone: value }))}
        >
          <SelectTrigger data-testid="timezone-select">
            <SelectValue placeholder="Select timezone" />
          </SelectTrigger>
          <SelectContent>
            {TIMEZONES.map(tz => (
              <SelectItem key={tz.value} value={tz.value}>
                {tz.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Hours by day */}
      <div className="space-y-4">
        <Label className="text-base font-medium">Operating Hours</Label>
        
        {DAYS.map((day, index) => (
          <div
            key={day}
            className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg"
            data-testid={`hours-${day}`}
          >
            <div className="w-28">
              <span className="font-medium">{formatDayLabel(day)}</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={formData[day]?.is_open}
                onCheckedChange={(checked) => handleDayChange(day, 'is_open', checked)}
                data-testid={`switch-${day}`}
              />
              <span className="text-sm text-gray-500">
                {formData[day]?.is_open ? 'Open' : 'Closed'}
              </span>
            </div>
            
            {formData[day]?.is_open && (
              <>
                <div className="flex items-center gap-2">
                  <Input
                    type="time"
                    value={formData[day]?.open_time || '09:00'}
                    onChange={(e) => handleDayChange(day, 'open_time', e.target.value)}
                    className="w-32"
                    data-testid={`open-time-${day}`}
                  />
                  <span className="text-gray-500">to</span>
                  <Input
                    type="time"
                    value={formData[day]?.close_time || '21:00'}
                    onChange={(e) => handleDayChange(day, 'close_time', e.target.value)}
                    className="w-32"
                    data-testid={`close-time-${day}`}
                  />
                </div>
                
                {index === 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copyToAll(day)}
                    className="ml-auto text-xs"
                  >
                    Copy to all
                  </Button>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex justify-between gap-3 pt-4">
        {onBack && (
          <Button type="button" variant="outline" onClick={onBack}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
        )}
        <Button
          type="submit"
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700 ml-auto"
          data-testid="save-store-hours-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            'Save & Continue'
          )}
        </Button>
      </div>
    </form>
  );
}
